import React, { useState, useEffect } from 'react';
import { ratingsAPI } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Star, X, MapPin, DollarSign, User, Truck, Building } from 'lucide-react';
import { toast } from 'sonner';

const StarRatingInput = ({ value, onChange, label }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="space-y-1">
      <Label className="text-sm text-gray-400">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="transition-transform hover:scale-110"
          >
            <Star
              className={`w-6 h-6 transition-colors ${
                star <= (hover || value)
                  ? 'text-[#4ADE80] fill-[#4ADE80]'
                  : 'text-gray-600'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const RatingPrompt = () => {
  const [pendingRatings, setPendingRatings] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  const [formData, setFormData] = useState({
    rating: 0,
    communication_rating: 0,
    timeliness_rating: 0,
    professionalism_rating: 0,
    review: '',
  });

  useEffect(() => {
    checkPendingRatings();
  }, []);

  const checkPendingRatings = async () => {
    try {
      const response = await ratingsAPI.getPending();
      if (response.data.count > 0) {
        setPendingRatings(response.data.pending_ratings);
        // Check if user previously dismissed (stored in session)
        const dismissedUntil = sessionStorage.getItem('rating_dismissed_until');
        if (!dismissedUntil || new Date() > new Date(dismissedUntil)) {
          setShowModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to check pending ratings');
    }
  };

  const handleSubmit = async () => {
    if (formData.rating === 0) {
      toast.error('Please select an overall rating');
      return;
    }

    const currentLoad = pendingRatings[currentIndex];
    setLoading(true);

    try {
      await ratingsAPI.create({
        load_id: currentLoad.load_id,
        rated_user_id: currentLoad.rate_user_id,
        rating: formData.rating,
        review: formData.review || null,
        communication_rating: formData.communication_rating || null,
        timeliness_rating: formData.timeliness_rating || null,
        professionalism_rating: formData.professionalism_rating || null,
      });

      toast.success('Rating submitted! Thank you for your feedback.');

      // Move to next rating or close
      if (currentIndex < pendingRatings.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setFormData({
          rating: 0,
          communication_rating: 0,
          timeliness_rating: 0,
          professionalism_rating: 0,
          review: '',
        });
      } else {
        setShowModal(false);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit rating';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    // Dismiss for 24 hours
    const dismissUntil = new Date();
    dismissUntil.setHours(dismissUntil.getHours() + 24);
    sessionStorage.setItem('rating_dismissed_until', dismissUntil.toISOString());
    setShowModal(false);
    setDismissed(true);
  };

  const handleSkip = () => {
    if (currentIndex < pendingRatings.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setFormData({
        rating: 0,
        communication_rating: 0,
        timeliness_rating: 0,
        professionalism_rating: 0,
        review: '',
      });
    } else {
      handleDismiss();
    }
  };

  if (pendingRatings.length === 0 || dismissed) return null;

  const currentLoad = pendingRatings[currentIndex];

  // Show banner if modal is closed but there are pending ratings
  if (!showModal && pendingRatings.length > 0) {
    return (
      <div className="fixed bottom-20 right-6 z-40 max-w-sm animate-slide-up">
        <div className="bg-gradient-to-r from-[#12151a] to-[#1a1d24] rounded-lg border border-[#4ADE80]/30 shadow-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#4ADE80]/20 flex items-center justify-center flex-shrink-0">
              <Star className="w-5 h-5 text-[#4ADE80]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white">
                Rate Your Experience
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                You have {pendingRatings.length} completed {pendingRatings.length === 1 ? 'load' : 'loads'} to rate
              </p>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={() => setShowModal(true)}
                  className="bg-[#4ADE80] hover:bg-[#22C55E] text-black text-xs h-7"
                >
                  Rate Now
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDismiss}
                  className="text-gray-400 text-xs h-7"
                >
                  Later
                </Button>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-gray-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className="bg-[#12151a] border-[#1a1d24] max-w-lg">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-[#4ADE80]" />
              Rate Your Experience
            </DialogTitle>
            {pendingRatings.length > 1 && (
              <span className="text-sm text-gray-500">
                {currentIndex + 1} of {pendingRatings.length}
              </span>
            )}
          </div>
          <DialogDescription>
            Your feedback helps build trust in the HaulLogix community
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Load Info Card */}
          <div className="bg-[#0a0c10] rounded-lg p-4 border border-[#1a1d24]">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-[#4ADE80]" />
              <span className="text-sm text-white">
                {currentLoad?.pickup_location} → {currentLoad?.delivery_location}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-gray-400">
                <DollarSign className="w-4 h-4" />
                <span>${currentLoad?.price?.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                {currentLoad?.rate_user_role === 'carrier' ? (
                  <Truck className="w-4 h-4 text-blue-400" />
                ) : (
                  <Building className="w-4 h-4 text-green-400" />
                )}
                <span className="text-white">{currentLoad?.rate_user_name}</span>
                {currentLoad?.rate_user_company && (
                  <span className="text-gray-500">({currentLoad.rate_user_company})</span>
                )}
              </div>
            </div>
          </div>

          {/* Overall Rating */}
          <div className="text-center">
            <Label className="text-base font-medium">Overall Rating</Label>
            <div className="flex justify-center gap-2 mt-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-10 h-10 transition-colors ${
                      star <= formData.rating
                        ? 'text-[#4ADE80] fill-[#4ADE80]'
                        : 'text-gray-600 hover:text-gray-500'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {formData.rating === 0 && 'Select a rating'}
              {formData.rating === 1 && 'Poor'}
              {formData.rating === 2 && 'Fair'}
              {formData.rating === 3 && 'Good'}
              {formData.rating === 4 && 'Very Good'}
              {formData.rating === 5 && 'Excellent'}
            </p>
          </div>

          {/* Detailed Ratings */}
          <div className="grid grid-cols-3 gap-4">
            <StarRatingInput
              label="Communication"
              value={formData.communication_rating}
              onChange={(val) => setFormData({ ...formData, communication_rating: val })}
            />
            <StarRatingInput
              label="Timeliness"
              value={formData.timeliness_rating}
              onChange={(val) => setFormData({ ...formData, timeliness_rating: val })}
            />
            <StarRatingInput
              label="Professionalism"
              value={formData.professionalism_rating}
              onChange={(val) => setFormData({ ...formData, professionalism_rating: val })}
            />
          </div>

          {/* Review Text */}
          <div className="space-y-2">
            <Label htmlFor="review">Write a Review (Optional)</Label>
            <Textarea
              id="review"
              placeholder="Share your experience working with this user..."
              value={formData.review}
              onChange={(e) => setFormData({ ...formData, review: e.target.value })}
              className="bg-[#0a0c10] border-[#1a1d24] min-h-[80px]"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="flex-1 text-gray-400"
              disabled={loading}
            >
              Skip
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 glow-gold"
              disabled={loading || formData.rating === 0}
            >
              {loading ? 'Submitting...' : 'Submit Rating'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RatingPrompt;
